import React, { useEffect, useState } from 'react';
import { Button, Select } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { previewOrderGenieStoredFile } from '../../services/orderXApi';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;
const ROW_PAGE_SIZE = 25;
const COLUMN_LIMIT_OPTIONS = [
  { value: 6, label: '6 columns' },
  { value: 10, label: '10 columns' },
  { value: 20, label: '20 columns' },
  { value: 50, label: '50 columns' },
  { value: 0, label: 'All columns' },
];

// Shared per-file preview body used by both DocumentPreviewPanel (inline, step2) and
// RequestSourcePreviewModal (full-screen expand) so PDF page navigation, the xlsx sheet
// picker, and the CSV/xlsx row/column controls only need to be implemented once.
const DocumentFilePreview = ({ file, maxHeight = '65vh' }) => {
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [zoom, setZoomState] = useState(1);
  const [rowLimit, setRowLimit] = useState(ROW_PAGE_SIZE);
  const [maxColumns, setMaxColumns] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const setZoom = (next) => setZoomState(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next)));

  const fetchPreview = async ({ selectedSheet, nextRowLimit, nextMaxColumns } = {}) => {
    try {
      const data = await previewOrderGenieStoredFile({
        storage_path: file.storage_path,
        filename: file.filename,
        content_type: file.content_type,
        selected_sheet: selectedSheet,
        row_offset: 0,
        row_limit: nextRowLimit ?? rowLimit,
        max_columns: (nextMaxColumns ?? maxColumns) || undefined,
      });
      setPreview(data);
      setPageIndex(0);
      return data;
    } catch (err) {
      const message = err?.message || 'Preview unavailable.';
      setLoadError(message);
      setPreview({ has_preview: false, error: message });
      return null;
    }
  };

  useEffect(() => {
    setPreview(null);
    setLoadError('');
    setPageIndex(0);
    setZoomState(1);
    setRowLimit(ROW_PAGE_SIZE);
    setMaxColumns(0);
    fetchPreview({ nextRowLimit: ROW_PAGE_SIZE, nextMaxColumns: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.storage_path, file.filename]);

  if (!preview) {
    return <div className="text-muted small">Loading preview…</div>;
  }
  if (loadError || preview.error || preview.has_preview === false) {
    return <div className="text-muted small">{preview.error || loadError || 'Preview unavailable for this file type.'}</div>;
  }

  const images = Array.isArray(preview.preview_images) && preview.preview_images.length > 0
    ? preview.preview_images
    : (preview.preview_image ? [preview.preview_image] : []);
  const pageCount = images.length;
  const sheetNames = Array.isArray(preview.sheet_names) ? preview.sheet_names : [];
  const hasTable = Array.isArray(preview.preview_table) && preview.preview_table.length > 0;

  const handleSheetChange = async (nextSheet) => {
    setLoadingMore(true);
    await fetchPreview({ selectedSheet: nextSheet, nextRowLimit: ROW_PAGE_SIZE, nextMaxColumns: maxColumns });
    setRowLimit(ROW_PAGE_SIZE);
    setLoadingMore(false);
  };

  const handleColumnLimitChange = async (nextMaxColumns) => {
    setLoadingMore(true);
    setMaxColumns(nextMaxColumns);
    await fetchPreview({ selectedSheet: preview.selected_sheet, nextRowLimit: rowLimit, nextMaxColumns });
    setLoadingMore(false);
  };

  const handleLoadMoreRows = async () => {
    const nextRowLimit = rowLimit + ROW_PAGE_SIZE;
    setLoadingMore(true);
    setRowLimit(nextRowLimit);
    await fetchPreview({ selectedSheet: preview.selected_sheet, nextRowLimit, nextMaxColumns: maxColumns });
    setLoadingMore(false);
  };

  return (
    <>
      {pageCount > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          {pageCount > 1 ? (
            <div className="d-flex align-items-center gap-1">
              <Button
                size="small"
                icon={<LeftOutlined />}
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                disabled={pageIndex <= 0}
                title="Previous page"
              />
              <span className="small text-muted" style={{ minWidth: 90, textAlign: 'center' }}>
                Page {pageIndex + 1} of {pageCount}
              </span>
              <Button
                size="small"
                icon={<RightOutlined />}
                onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
                disabled={pageIndex >= pageCount - 1}
                title="Next page"
              />
            </div>
          ) : <span />}
          <div className="d-flex align-items-center gap-1">
            <Button
              size="small"
              icon={<ZoomOutOutlined />}
              onClick={() => setZoom(zoom - ZOOM_STEP)}
              disabled={zoom <= ZOOM_MIN}
              title="Zoom out"
            />
            <span className="small text-muted" style={{ width: 40, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <Button
              size="small"
              icon={<ZoomInOutlined />}
              onClick={() => setZoom(zoom + ZOOM_STEP)}
              disabled={zoom >= ZOOM_MAX}
              title="Zoom in"
            />
            <Button
              size="small"
              icon={<UndoOutlined />}
              onClick={() => setZoom(1)}
              disabled={zoom === 1}
              title="Reset zoom"
            />
          </div>
        </div>
      )}
      {pageCount > 0 && (
        <div
          style={{ maxHeight, overflow: 'auto', border: '1px solid #eee', borderRadius: 8 }}
          onWheel={(e) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            setZoom(zoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
          }}
        >
          <img
            src={images[pageIndex]}
            alt={`${file.filename} — page ${pageIndex + 1}`}
            style={{
              display: 'block',
              width: `${zoom * 100}%`,
              maxWidth: zoom <= 1 ? '100%' : 'none',
            }}
          />
        </div>
      )}
      {pageCount === 0 && hasTable && (
        <div className="d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            {sheetNames.length > 1 ? (
              <Select
                size="small"
                style={{ minWidth: 160 }}
                value={preview.selected_sheet}
                onChange={handleSheetChange}
                options={sheetNames.map((name) => ({ value: name, label: name }))}
              />
            ) : <span />}
            <Select
              size="small"
              style={{ minWidth: 130 }}
              value={maxColumns}
              onChange={handleColumnLimitChange}
              options={COLUMN_LIMIT_OPTIONS}
            />
          </div>
          <div style={{ maxHeight, overflow: 'auto' }}>
            <table className="table table-sm mb-0">
              <tbody>
                {preview.preview_table.map((row, rowIdx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={cellIdx}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">
              Showing {preview.preview_table.length} of {preview.preview_table_total_rows ?? preview.preview_table.length} rows
            </span>
            {preview.preview_table_has_more && (
              <Button size="small" loading={loadingMore} onClick={handleLoadMoreRows}>
                Load more rows
              </Button>
            )}
          </div>
        </div>
      )}
      {pageCount === 0 && !hasTable && preview.preview_text && (
        <pre className="small" style={{ maxHeight, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
          {preview.preview_text}
        </pre>
      )}
    </>
  );
};

export default DocumentFilePreview;
